import React, { useState, useCallback, useEffect } from 'react';
import { DatePicker, TimeInput, FormField, Box } from '@amzn/awsui-components-react-v3';

interface DateRangePickerProps {
    startDateTime?: Date;
    endDateTime?: Date;
    onStartDateTimeChange: (dateTime: Date) => void;
    onEndDateTimeChange: (dateTime: Date) => void;
    maxDate?: Date;
    minDate?: Date;
    errorMessage?: string;
    timezoneLabel?: string; // display-only (e.g., "UTC")
}

const pad2 = (n: number) => n.toString().padStart(2, '0');

const formatDateForAwsUIUTC = (date: Date): string =>
    `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;

const formatTimeForAwsUIUTC = (date: Date): string =>
    `${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`;

const parseDateTimeFromAwsUIUTC = (dateStr: string, timeStr: string): Date => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const [hh, mm] = timeStr.split(':').map(Number);
    return new Date(Date.UTC(y, m - 1, d, hh, mm));
};

const DateRangePicker: React.FC<DateRangePickerProps> = ({
                                                             startDateTime = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000),
                                                             endDateTime = new Date(),
                                                             onStartDateTimeChange,
                                                             onEndDateTimeChange,
                                                             maxDate,
                                                             minDate,
                                                             errorMessage = 'Invalid date range',
                                                             timezoneLabel = 'UTC',
                                                         }) => {
    const [startDate, setStartDate] = useState<string>(formatDateForAwsUIUTC(startDateTime));
    const [startTime, setStartTime] = useState<string>(formatTimeForAwsUIUTC(startDateTime));
    const [endDate, setEndDate] = useState<string>(formatDateForAwsUIUTC(endDateTime));
    const [endTime, setEndTime] = useState<string>(formatTimeForAwsUIUTC(endDateTime));
    const [isValid, setIsValid] = useState(true);

    const validateDateRange = useCallback(
        (startDateStr: string, startTimeStr: string, endDateStr: string, endTimeStr: string) => {
            try {
                const startUTC = parseDateTimeFromAwsUIUTC(startDateStr, startTimeStr);
                const endUTC = parseDateTimeFromAwsUIUTC(endDateStr, endTimeStr);

                let ok = startUTC < endUTC;
                if (ok && minDate) ok = startUTC >= minDate;
                if (ok && maxDate) ok = endUTC <= maxDate;

                setIsValid(ok);
                if (ok) {
                    onStartDateTimeChange(startUTC);
                    onEndDateTimeChange(endUTC);
                }
                return ok;
            } catch {
                setIsValid(false);
                return false;
            }
        },
        [onStartDateTimeChange, onEndDateTimeChange, minDate, maxDate]
    );

    useEffect(() => {
        const newStartDate = formatDateForAwsUIUTC(startDateTime);
        const newStartTime = formatTimeForAwsUIUTC(startDateTime);
        const newEndDate = formatDateForAwsUIUTC(endDateTime);
        const newEndTime = formatTimeForAwsUIUTC(endDateTime);

        if (
            startDate !== newStartDate ||
            startTime !== newStartTime ||
            endDate !== newEndDate ||
            endTime !== newEndTime
        ) {
            setStartDate(newStartDate);
            setStartTime(newStartTime);
            setEndDate(newEndDate);
            setEndTime(newEndTime);
            setIsValid(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDateTime.getTime(), endDateTime.getTime()]);

    const handleStartDateChange = (e: any) => {
        const v = e.detail.value as string;
        setStartDate(v);
        validateDateRange(v, startTime, endDate, endTime);
    };

    const handleStartTimeChange = (e: any) => {
        const v = e.detail.value as string;
        setStartTime(v);
        validateDateRange(startDate, v, endDate, endTime);
    };

    const handleEndDateChange = (e: any) => {
        const v = e.detail.value as string;
        setEndDate(v);
        validateDateRange(startDate, startTime, v, endTime);
    };

    const handleEndTimeChange = (e: any) => {
        const v = e.detail.value as string;
        setEndTime(v);
        validateDateRange(startDate, startTime, endDate, v);
    };

    return (
        <div className="date-range-picker-wrapper">
            <div className="date-range-description">
                <strong>Date Range ({timezoneLabel} Timezone)</strong>
            </div>

            <div className="date-range-horizontal-container">
                <div className="date-time-group">
                    <div className="date-time-inputs">
                        <FormField label="Start Date">
                            <DatePicker onChange={handleStartDateChange} value={startDate} placeholder="YYYY-MM-DD" />
                        </FormField>
                        <FormField label="Start Time">
                            <TimeInput onChange={handleStartTimeChange} value={startTime} format="hh:mm" />
                        </FormField>
                    </div>
                </div>

                <div className="date-time-group">
                    <div className="date-time-inputs">
                        <FormField label="End Date">
                            <DatePicker onChange={handleEndDateChange} value={endDate} placeholder="YYYY-MM-DD" />
                        </FormField>
                        <FormField label="End Time">
                            <TimeInput onChange={handleEndTimeChange} value={endTime} format="hh:mm" />
                        </FormField>
                    </div>
                </div>
            </div>

            {!isValid && (
                <Box color="text-status-error">
                    <strong>{errorMessage}</strong>
                </Box>
            )}
        </div>
    );
};

export default DateRangePicker;

